import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, Shield, Play, RotateCcw, CheckCircle2, Info, ArrowLeft, ArrowRight, Eye, Loader2 } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useFaceDetection } from './useFaceDetection';
import Container from '../common/Container';
import Card from '../common/Card';
import Button from '../common/Button';
import StatusBadge from '../common/StatusBadge';
import MetricsDisplay from '../common/MetricsDisplay';
import ProgressBar from '../common/ProgressBar';
import VideoCanvas from '../common/VideoCanvas';
import ChallengeList from './ChallengeList';
const API_KEY = process.env.REACT_APP_API_KEY;
// ✅ Constants outside component
const INITIAL_CHALLENGES = [
  { id: 1 , type: 'yaw', threshold: -25, instruction: 'turnLeft', icon: 'ArrowLeft', text: { en: 'Turn head LEFT', fa: 'سر را به چپ بچرخانید' } },
  { id: 2 , type: 'yaw', threshold: 25, instruction: 'turnRight', icon: 'ArrowRight', text: { en: 'Turn head RIGHT', fa: 'سر را به راست بچرخانید' } },
  { id: 3 , type: 'blink', requiredBlinks: 2, instruction: 'blinkTwice', icon: 'Eye', text: { en: 'Blink TWICE', fa: 'دو بار پلک بزنید' } }
];

const LivenessDetection = ({ onComplete , sessionIdP="" , userIdP="" }) => {
  const { t , lang } = useAppContext();
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const completingRef = useRef(false);
  
  // Session & Auth
  const [userId, setUserId] = useState(userIdP);
  const [sessionId, setSessionId] = useState(sessionIdP);
  const [sessionError, setSessionError] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  
  // UI States
  const [status, setStatus] = useState({ type: 'info', message: lang === 'fa' ? 'در حال راه‌اندازی...' : 'Initializing...' });
  const [metrics, setMetrics] = useState({});
  const metricsRef = useRef({});
  const [isUploading, setIsUploading] = useState(false);
  
  // Liveness States
  const [livenessStage, setLivenessStage] = useState('idle'); // idle, active, complete
  const [currentChallengeIndex, setCurrentChallengeIndex] = useState(-1);
  const [challengesPassed, setChallengesPassed] = useState([]);
  const [blinkCount, setBlinkCount] = useState(0);
  const [lastEyeState, setLastEyeState] = useState('open');
  const [challengeStartTime, setChallengeStartTime] = useState(null);
  const [challengeCooldown, setChallengeCooldown] = useState(false);
  
  // Blink Detection Constants
  const EAR_THRESHOLD = 0.23;
  const EAR_WINDOW = 3;
  const EAR_MIN_FRAMES = 2;
  const CHALLENGE_TIMEOUT = 15000;
  
  // Refs for stable access
  const livenessStageRef = useRef(livenessStage);
  const currentIndexRef = useRef(currentChallengeIndex);
  const blinkCountRef = useRef(blinkCount);
  const challengeCooldownRef = useRef(challengeCooldown);
  const challengeStartTimeRef = useRef(challengeStartTime);
  const earHistoryRef = useRef([]);
  const earBelowCountRef = useRef(0);
  const blinkActiveRef = useRef(false);
  
  // Challenges
  const [challenges, setChallenges] = useState(INITIAL_CHALLENGES);
  const challengesRef = useRef(challenges);
  
  // Face Detection Model
  const { model, isLoading: modelLoading, error: modelError } = useFaceDetection();

  // Icon component mapper
  const getIconComponent = (iconName) => {
    const icons = {
      ArrowLeft,
      ArrowRight,
      Eye
    };
    return icons[iconName] || null;
  };

  // ============================================
  // SESSION INITIALIZATION
  // ============================================
  useEffect(() => {
    const storedSessionId = localStorage.getItem('session_id');
    const storedUserId = localStorage.getItem('user_id');
    
    if (!storedSessionId || !storedUserId) {
      console.error('❌ No session found in localStorage');
      setSessionError(true);
      setStatus({ 
        type: 'error', 
        message: lang === 'fa' ? 'جلسه معتبری یافت نشد. لطفاً ابتدا احراز هویت کنید.' : 'No valid session. Please authenticate first.'
      });
      return;
    }
    
    setSessionId(storedSessionId);
    setUserId(storedUserId);
    setSessionError(false);
    
    // Shuffle challenges on mount
    const shuffled = [...INITIAL_CHALLENGES].sort(() => Math.random() - 0.5);
    setChallenges(shuffled);
  }, [lang]);

  // ============================================
  // METRICS UPDATE
  // ============================================
  useEffect(() => {
    const id = setInterval(() => {
      setMetrics(metricsRef.current);
    }, 500);
    return () => clearInterval(id);
  }, []);

  // ============================================
  // CAMERA INITIALIZATION
  // ============================================
  useEffect(() => {
    let stream = null;
    
    const initCamera = async () => {
      try {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('Camera error:', err);
        setStatus({ type: 'error', message: lang === 'fa' ? `خطای دوربین: ${err.message}` : `Camera error: ${err.message}` });
      }
    };
    
    if (!sessionError) {
      initCamera();
    }
    
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [sessionError, lang]);

  // ============================================
  // REF SYNCING
  // ============================================
  useEffect(() => { livenessStageRef.current = livenessStage; }, [livenessStage]);
  useEffect(() => { currentIndexRef.current = currentChallengeIndex; }, [currentChallengeIndex]);
  useEffect(() => { blinkCountRef.current = blinkCount; }, [blinkCount]);
  useEffect(() => { challengeCooldownRef.current = challengeCooldown; }, [challengeCooldown]);
  useEffect(() => { challengeStartTimeRef.current = challengeStartTime; }, [challengeStartTime]);
  useEffect(() => { challengesRef.current = challenges; }, [challenges]);

  // ============================================
  // MODEL STATUS
  // ============================================
  useEffect(() => {
    if (!modelLoading && !modelError && model) {
      setStatus({ type: 'success', message: lang === 'fa' ? 'آماده شروع تأیید هویت' : 'Ready to start verification' });
    } else if (modelError) {
      setStatus({ type: 'error', message: lang === 'fa' ? `خطای مدل: ${modelError}` : `Model error: ${modelError}` });
    } else if (modelLoading) {
      setStatus({ type: 'info', message: lang === 'fa' ? 'در حال بارگذاری مدل هوش مصنوعی...' : 'Loading AI model...' });
    }
  }, [modelLoading, modelError, model, lang]);

  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  const drawFaceLandmarks = useCallback((keypoints) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "rgba(0,0,0,0.05)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#008a5e";
    for (let i = 0; i < keypoints.length; i++) {
      const [x, y] = keypoints[i];
      ctx.beginPath();
      ctx.arc(x, y, 1.8, 0, 2 * Math.PI);
      ctx.fill();
    }
  }, []);

  const calculateEAR = useCallback((landmarks) => {
    try {
      const leftEye = [33, 160, 158, 133, 153, 144].map(i => landmarks[i]);
      const rightEye = [263, 387, 385, 362, 380, 373].map(i => landmarks[i]);
      
      const eyeAspectRatio = (eye) => {
        const v1 = Math.hypot(eye[1][0] - eye[5][0], eye[1][1] - eye[5][1]);
        const v2 = Math.hypot(eye[2][0] - eye[4][0], eye[2][1] - eye[4][1]);
        const h = Math.hypot(eye[0][0] - eye[3][0], eye[0][1] - eye[3][1]);
        return (v1 + v2) / (2.0 * h);
      };
      
      const leftEAR = eyeAspectRatio(leftEye);
      const rightEAR = eyeAspectRatio(rightEye);
      return (leftEAR + rightEAR) / 2;
    } catch (e) {
      return 0.25;
    }
  }, []);

  const pushAndGetSmoothedEAR = (ear) => {
    const buf = earHistoryRef.current;
    buf.push(ear);
    if (buf.length > EAR_WINDOW) buf.shift();
    const sum = buf.reduce((a, b) => a + b, 0);
    return sum / buf.length;
  };

  const resetLiveness = useCallback(() => {
    setLivenessStage('idle');
    setCurrentChallengeIndex(-1);
    setChallengesPassed([]);
    setBlinkCount(0);
    setLastEyeState('open');
    setChallengeStartTime(null);
    setChallengeCooldown(false);
    completingRef.current = false;
  }, []);

  // ============================================
  // CHALLENGE COMPLETION
  // ============================================
  const completeChallengeOnce = useCallback(() => {
    if (completingRef.current) return;
    completingRef.current = true;

    setChallengeCooldown(true);
    setChallengesPassed(prev => [
      ...prev,
      challengesRef.current[currentIndexRef.current].id
    ]);
    setStatus({ type: "success", message: lang === 'fa' ? "چالش با موفقیت انجام شد!" : "Challenge Complete!" });

    setTimeout(() => {
      completingRef.current = false;
      const nextIndex = currentIndexRef.current + 1;
      if (nextIndex < challengesRef.current.length) {
        setChallengeCooldown(false);
        setCurrentChallengeIndex(nextIndex);
        setBlinkCount(0);
        setLastEyeState("open");
        setChallengeStartTime(Date.now());
        const nextChallenge = challengesRef.current[nextIndex];
        const IconComponent = getIconComponent(nextChallenge.icon);
        setStatus({
          type: "warning",
          message: nextChallenge.text[lang]
        });
      } else {
        setLivenessStage("complete");
        setStatus({ type: "success", message: lang === 'fa' ? "همه چالش‌ها با موفقیت انجام شد!" : "All challenges passed!" });
      }
    }, 1500);
  }, [lang]);

  // ============================================
  // FACE DETECTION LOOP
  // ============================================
  useEffect(() => {
    if (!model || !videoRef.current || sessionError) return;

    const updateCanvasSize = () => {
  const video = videoRef.current;
  const canvas = canvasRef.current;
  if (!video || !canvas || !video.videoWidth || !video.videoHeight) return;

  // ✅ Resolution کانواس (برای کیفیت drawing)
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  // ✅ Display size کانواس (باید با rendered video size یکی باشه)
  const rect = video.getBoundingClientRect();
  canvas.style.width = `${rect.width}px`;
  canvas.style.height = `${rect.height}px`;
};

    const checkCurrentChallengeRefSafe = (yaw, ear) => {
      if (
        livenessStageRef.current !== "active" ||
        currentIndexRef.current < 0 ||
        challengeCooldownRef.current
      ) return;

      const challenge = challengesRef.current[currentIndexRef.current];
      if (!challenge) return;

      if (Date.now() - (challengeStartTimeRef.current || 0) > CHALLENGE_TIMEOUT) {
        setStatus({ type: "error", message: lang === 'fa' ? "زمان تمام شد" : "Timeout" });
        resetLiveness();
        return;
      }

      let passed = false;

      if (challenge.type === "yaw") {
        setStatus({
          type: "warning",
          message: `${challenge.text[lang]} (${yaw.toFixed(1)}° / ${challenge.threshold}°)`
        });
        passed =
          challenge.threshold < 0
            ? yaw < challenge.threshold
            : yaw > challenge.threshold;
      } else if (challenge.type === "blink") {
        setStatus({
          type: "warning",
          message: `${challenge.text[lang]} (${blinkCountRef.current}/${challenge.requiredBlinks})`
        });
        if (blinkCountRef.current >= challenge.requiredBlinks) passed = true;
      }

      if (passed) completeChallengeOnce();
    };

    let frameCount = 0;
    const onResults = (results) => {
      if (++frameCount % 2 !== 0) return;
      const canvas = canvasRef.current;
      const video = videoRef.current;
      if (!canvas || !video) return;

      updateCanvasSize();

      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "rgba(0,0,0,0.05)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (!results.multiFaceLandmarks || results.multiFaceLandmarks.length === 0) {
        if (livenessStageRef.current === "active") {
          setStatus({ type: "error", message: lang === 'fa' ? "چهره‌ای شناسایی نشد" : "No face detected" });
        }
        return;
      }

      const landmarks = results.multiFaceLandmarks[0];
      const keypoints = landmarks.map((p) => [p.x * canvas.width, p.y * canvas.height]);

      if (frameCount % 2 === 0) {
        drawFaceLandmarks(keypoints);
      }

      const noseTip = keypoints[1];
      const leftCheek = keypoints[234];
      const rightCheek = keypoints[454];
      const dLeft = Math.hypot(noseTip[0] - leftCheek[0], noseTip[1] - leftCheek[1]);
      const dRight = Math.hypot(noseTip[0] - rightCheek[0], noseTip[1] - rightCheek[1]);
      const yawDegApprox = ((dRight - dLeft) / (dRight + dLeft)) * 40;

      const rawEAR = calculateEAR(keypoints);
      const ear = pushAndGetSmoothedEAR(rawEAR);

      if (ear < EAR_THRESHOLD) {
        earBelowCountRef.current++;
        if (!blinkActiveRef.current && earBelowCountRef.current >= EAR_MIN_FRAMES) {
          blinkActiveRef.current = true;
          setBlinkCount(prev => prev + 1);
        }
      } else {
        earBelowCountRef.current = 0;
        blinkActiveRef.current = false;
      }

      metricsRef.current = {
        [lang === 'fa' ? "زاویه سر" : "Head Angle"]: `${yawDegApprox.toFixed(1)}°`,
        "EAR": ear.toFixed(3),
        [lang === 'fa' ? "پلک زدن" : "Blinks"]: blinkCountRef.current,
      };

      checkCurrentChallengeRefSafe(yawDegApprox, ear);
    };

    window.addEventListener("resize", updateCanvasSize);
    model.onResults(onResults);

    let lastSent = 0;
    
    const onFrameThrottled = async () => {
      const now = performance.now();
      if (now - lastSent < 50) return;
      lastSent = now;

      try {
        await model.send({ image: videoRef.current });
      } catch (err) {
        // ignore transient errors
      }
    };
    
    // ✅ Camera با resolution ثابت
    const camera = new window.Camera(videoRef.current, {
      onFrame: onFrameThrottled,
      width: 640,
      height: 480,
    });

    camera.start();

    return () => {
      window.removeEventListener("resize", updateCanvasSize);
      try {
        camera.stop();
      } catch {}
    };
  }, [model, sessionError, calculateEAR, drawFaceLandmarks, completeChallengeOnce, resetLiveness, lang]);

  // ============================================
  // ACTION HANDLERS
  // ============================================
  const handleStartLiveness = () => {
    if (!userId || !sessionId) {
      setStatus({ type: 'error', message: lang === 'fa' ? 'جلسه معتبری وجود ندارد' : 'No valid session' });
      return;
    }
    
    setLivenessStage('active');
    setChallengesPassed([]);
    setCurrentChallengeIndex(0);
    setBlinkCount(0);
    setLastEyeState('open');
    setChallengeStartTime(Date.now());
    setStatus({ type: 'warning', message: challenges[0].text[lang] });
  };

  const handleCapture = async () => {
    const video = videoRef.current;
    if (!video) return;
    
    const storedSessionId = localStorage.getItem('session_id');
    const storedUserId = localStorage.getItem('user_id');
    
    setIsUploading(true);
    setStatus({ type: 'info', message: lang === 'fa' ? 'در حال ارسال...' : 'Uploading...' });
    
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    
    const dataURL = canvas.toDataURL('image/jpeg', 0.9);
    const API_BASE = process.env.REACT_APP_API_BASE || "https://clankless-inflatedly-danae.ngrok-free.dev";
    
    try {
      const res = await fetch(`${API_BASE}/verify_selfie`, {
        method: "POST",
        headers: { "Content-Type": "application/json" , "x-api-key": API_KEY },
        body: JSON.stringify({
          image: dataURL,
          session_id: storedSessionId,
          liveness_passed: true,
          challenges_completed: challenges.map(c => c.text[lang]).join(", "),
        }),
      });
      
      const result = await res.json();
      
      if (result.status === "SUCCESS") {
        setStatus({ type: "success", message: lang === 'fa' ? "تأیید هویت موفق!" : "Verification successful!" });
        setTimeout(() => {
          if (onComplete) onComplete(); 
        }, 1000);
      } else {
        setStatus({ type: 'error', message: lang === 'fa' ? `ناموفق: ${result.reason || 'نامشخص'}` : `Failed: ${result.reason || 'unknown'}` });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setStatus({ type: 'error', message: lang === 'fa' ? `خطا: ${error.message}` : `Error: ${error.message}` });
    } finally {
      setIsUploading(false);
    }
  };

  const handleManualSession = () => {
    const testSessionId = `test-session-${Date.now()}`;
    const testUserId = `test-user-${Date.now()}`;
    
    localStorage.setItem('session_id', testSessionId);
    localStorage.setItem('user_id', testUserId);
    
    setSessionId(testSessionId);
    setUserId(testUserId);
    setSessionError(false);
    setStatus({ type: 'success', message: lang === 'fa' ? 'جلسه تست ایجاد شد!' : 'Test session created!' });
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <Container>
      {/* Header Section with Step Indicator */}
      <Card variant="elevated" className="mb-6 p-6">
        <div className="flex flex-col items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-accent-green/20 flex items-center justify-center">
                <Shield className="text-accent-green" size={20} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-text-light">
                  {lang === 'fa' ? 'تأیید زنده بودن چهره' : 'Face Liveness Verification'}
                </h1>
                <p className="text-sm text-text-gray mt-1">
                  {lang === 'fa' ? 'مرحله 1 از 4 - فرآیند تأیید هویت' : 'Step 1 of 4 - Identity Verification Process'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Step Progress Indicator */}
          <div className="flex gap-2">
            <div className="w-12 h-1 bg-accent-green rounded-full"></div>
            <div className="w-12 h-1 bg-accent-green/20 rounded-full"></div>
            <div className="w-12 h-1 bg-accent-green/20 rounded-full"></div>
            <div className="w-12 h-1 bg-accent-green/20 rounded-full"></div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-accent-green/10 border-2 border-accent-green/30 rounded-xl p-4 flex gap-3">
          <Info className="text-accent-green flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-text-light">
            <p className="font-semibold mb-1">
              {lang === 'fa' ? 'تشخیص زنده بودن چیست؟' : 'What is liveness detection?'}
            </p>
            <p className="text-text-gray">
              {lang === 'fa' 
                ? 'ما با درخواست انجام حرکات ساده سر و اعمال چهره، تأیید می‌کنیم که شما یک فرد واقعی هستید. این کار از تقلب جلوگیری کرده و امنیت حساب را تضمین می‌کند.'
                : 'We\'ll verify you\'re a real person by asking you to complete simple head movements and facial actions. This prevents fraud and ensures account security.'
              }
            </p>
          </div>
        </div>

        {/* Loading Indicator برای مدل */}
        {modelLoading && (
          <div className="mt-4 bg-accent-golden/10 border-2 border-accent-golden/30 rounded-xl p-4 flex gap-3 items-center">
            <Loader2 className="text-accent-golden animate-spin flex-shrink-0" size={20} />
            <div className="text-sm text-text-light">
              <p className="font-semibold">
                {lang === 'fa' ? 'در حال بارگذاری مدل هوش مصنوعی...' : 'Loading AI Model...'}
              </p>
              <p className="text-text-gray text-xs mt-1">
                {lang === 'fa' ? 'لطفاً صبر کنید، این ممکن است چند لحظه طول بکشد' : 'Please wait, this may take a few moments'}
              </p>
            </div>
          </div>
        )}

        {userId && (
          <div className="mt-4 pt-4 border-t border-accent-green/20">
            <p className="text-xs text-text-gray">
              {lang === 'fa' ? 'شناسه کاربر:' : 'User ID:'} <span className="text-accent-golden font-mono">{userId.slice(0, 16)}...</span>
            </p>
          </div>
        )}

        {sessionError && (
          <div className="mt-4">
            <div className="bg-accent-red/10 border-2 border-accent-red/30 rounded-xl p-4 mb-3">
              <p className="text-sm text-accent-red mb-3">
                {lang === 'fa' 
                  ? 'جلسه فعالی یافت نشد. برای آزمایش، می‌توانید یک جلسه موقت ایجاد کنید.'
                  : 'No active session found. For testing purposes, you can create a temporary session.'
                }
              </p>
              <Button onClick={handleManualSession} variant="secondary" size="sm" fullWidth>
                {lang === 'fa' ? 'ایجاد جلسه تست' : 'Create Test Session'}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {!sessionError && (
        <>
          {/* Video Feed Section */}
          <Card variant="elevated" className="mb-2 p-2" id="video-container">
            <VideoCanvas
              videoRef={videoRef}
              canvasRef={canvasRef}
            />
          </Card>
          {/* Status Display */}
          <div className="mb-6">
            <StatusBadge type={status.type} showIcon={true}>
              {status.message}
            </StatusBadge>
          </div>
          {/* Progress Bar (Only during active verification) */}
          {livenessStage === 'active' && (
            <Card className="mb-6 p-5">
              <ProgressBar
                current={challengesPassed.length}
                total={challenges.length}
                label={lang === 'fa' ? 'پیشرفت تأیید هویت' : 'VERIFICATION PROGRESS'}
              />
            </Card>
          )}

          {/* Metrics Display (Only during active) */}
          {livenessStage === 'active' && Object.keys(metrics).length > 0 && (
            <Card className="mb-6 p-5">
              <h3 className="text-sm font-semibold text-text-gray uppercase tracking-wider mb-3">
                {lang === 'fa' ? 'معیارهای زنده' : 'Live Metrics'}
              </h3>
              <MetricsDisplay metrics={metrics} />
            </Card>
          )}

          {/* Action Buttons */}
          <div className="space-y-3 mb-6">
            {livenessStage === 'idle' && (
              <Button 
                onClick={handleStartLiveness} 
                variant="primary"
                size="lg"
                fullWidth
                disabled={modelLoading || !userId || !model}
              >
                {modelLoading ? (
                  <>
                    <Loader2 className="inline mr-2 animate-spin" size={20} />
                    {lang === 'fa' ? 'در حال بارگذاری مدل...' : 'Loading Model...'}
                  </>
                ) : (
                  <>
                    <Play className="inline mr-2" size={20} />
                    {lang === 'fa' ? 'شروع تأیید هویت' : 'Start Verification'}
                  </>
                )}
              </Button>
            )}
            
            {livenessStage === 'complete' && (
              <>
                <Button 
                  onClick={handleCapture} 
                  variant="primary" 
                  size="lg" 
                  fullWidth
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="inline mr-2 animate-spin" size={20} />
                      {lang === 'fa' ? 'در حال ارسال...' : 'Uploading...'}
                    </>
                  ) : (
                    <>
                      <Camera className="inline mr-2" size={20} />
                      {lang === 'fa' ? 'عکس بگیرید و ارسال کنید' : 'Capture & Submit Photo'}
                    </>
                  )}
                </Button>
                <Button 
                  onClick={resetLiveness} 
                  variant="outline" 
                  size="md" 
                  fullWidth
                  disabled={isUploading}
                >
                  <RotateCcw className="inline mr-2" size={18} />
                  {lang === 'fa' ? 'شروع مجدد تأیید هویت' : 'Restart Verification'}
                </Button>
              </>
            )}
          </div>

          {/* Instructions / Challenge List */}
          {livenessStage === 'idle' && (
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="text-accent-green" size={20} />
                <h3 className="font-semibold text-text-light">
                  {lang === 'fa' ? 'مراحل تأیید هویت' : 'Verification Steps'}
                </h3>
              </div>
              <p className="text-sm text-text-gray mb-4">
                {lang === 'fa' 
                  ? 'از شما خواسته می‌شود این اعمال ساده را برای تأیید اینکه یک فرد واقعی هستید، انجام دهید:'
                  : 'You\'ll be asked to complete these simple actions to verify you\'re a real person:'
                }
              </p>
              <ChallengeList
                challenges={challenges}
                completedIds={[]}
                currentIndex={-1}
                translations={t}
                lang={lang}
                getIconComponent={getIconComponent}
              />
              <div className="mt-4 pt-4 border-t border-accent-green/20">
                <p className="text-xs text-text-gray text-center">
                  {lang === 'fa' 
                    ? 'نکته: از وجود نور کافی و کیفیت تصویر اطمینان حاصل کنید'
                    : 'Tip: Ensure good lighting and your face is clearly visible'
                  }
                </p>
              </div>
            </Card>
          )}

          {livenessStage === 'active' && (
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="text-accent-golden" size={20} />
                <h3 className="font-semibold text-text-light">
                  {lang === 'fa' ? 'چالش‌های فعال' : 'Active Challenges'}
                </h3>
              </div>
              <ChallengeList
                challenges={challenges}
                completedIds={challengesPassed}
                currentIndex={currentChallengeIndex}
                translations={t}
                lang={lang}
                getIconComponent={getIconComponent}
              />
            </Card>
          )}

          {livenessStage === 'complete' && (
            <Card className="p-6 bg-accent-green/5 border-accent-green/50">
              <div className="text-center">
                <div className="w-16 h-16 bg-accent-green/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="text-accent-green" size={32} />
                </div>
                <h3 className="text-xl font-bold text-accent-green mb-2">
                  {lang === 'fa' ? 'تأیید هویت کامل شد!' : 'Verification Complete!'}
                </h3>
                <p className="text-text-gray text-sm">
                  {lang === 'fa' 
                    ? 'تمام بررسی‌های زنده بودن با موفقیت انجام شد. دکمه بالا را برای گرفتن و ارسال عکس خود کلیک کنید.'
                    : 'All liveness checks passed successfully. Click the button above to capture and submit your photo.'
                  }
                </p>
              </div>
            </Card>
          )}
        </>
      )}
    </Container>
  );
};

export default LivenessDetection;